import { useI18n } from "vue-i18n";

/**
 * Composable for getting localized image paths.
 * Logic: Checks if a localized image exists in the project for the current locale.
 * If not, falls back to the 'en' version.
 */
export const useLocalizedImage = () => {
  const { locale, locales } = useI18n();

  /**
   * Vite feature to get a list of all files in public directory at build time.
   * We use a broad pattern to catch all possible localized images without hardcoding language codes.
   */
  const ALL_IMAGES = import.meta.glob([
    "../public/images/**/*.{png,jpg,jpeg,svg,webp}",
    "../public/*.{png,jpg,jpeg,svg,webp}"
  ], { eager: true });

  // Normalize paths: remove "../public" suffix to match the final URLs
  const EXISTING_PATHS = Object.keys(ALL_IMAGES).map(key => key.replace("../public", ""));

  /**
   * Returns the path to the localized image.
   * @param basePath - Base path without locale and extension (e.g., '/images/hero/beauty-nail-hero')
   * @param extension - File extension (default: 'png')
   * @param separator - Separator before locale (default: '-')
   * @param postSuffix - Suffix to append after locale (e.g., '-desktop')
   * @returns Full path with locale suffix or fallback to 'en'
   */
  const getLocalizedImagePath = (
    basePath: string,
    extension: string = "png",
    separator: string = "-",
    postSuffix: string = "",
  ) => {
    const currentLocale = locale.value;

    // 1. Try to find image for the current locale
    const localizedPath = `${basePath}${separator}${currentLocale}${postSuffix}.${extension}`;
    if (EXISTING_PATHS.includes(localizedPath)) {
      return localizedPath;
    }

    // 2. Fallback to English ('en') if localized version doesn't exist
    const fallbackPath = `${basePath}${separator}en${postSuffix}.${extension}`;
    if (EXISTING_PATHS.includes(fallbackPath)) {
      return fallbackPath;
    }

    // 3. Last resort: return the path for current locale even if file is missing (to let browser try it)
    return localizedPath;
  };

  return {
    getLocalizedImagePath,
    supportedLocales: locales,
  };
};
